<template>
    <EditComponent
        dialog-id="editEducationDialog"
        open-dialog-event="educations:edit-mode"
        updated-event="educations:updated"
        :open-dialog-post-function="initEducation"
        :confirm-function="update"
        :track-section-opened="EditionOpenedSection.EDUCATIONS"
        :track-existing-element="true"
    >
        <template #edit-header>{{ t('profile.section.educations.title.v2') }}</template>
        <template #edit-body>
            <VJoyWrapper direction="column" align="stretch" class="profile-education__list-item">
                <VJoyInput
                    v-model="element.description"
                    name="educationDescription"
                    :required="true"
                    :invalid="r$.description.$error"
                    :placeholder="t('profile.new.education.description')"
                >
                    {{ t('profile.new.education.description') }}
                </VJoyInput>
                <VJoyFormError v-if="r$.description.$error" data-testid="profile-edition-section-education-description-error">
                    {{ t('form.field.error.message.required.generic') }}
                </VJoyFormError>
                <VJoyInput
                    v-model="element.title"
                    name="educationTitle"
                    :required="true"
                    :invalid="r$.title.$error"
                    :placeholder="t('profile.new.education.title')"
                >
                    {{ t('profile.new.education.title') }}
                </VJoyInput>
                <VJoyFormError v-if="r$.title.$error" data-testid="profile-edition-section-education-title-error">
                    {{ t('form.field.error.message.required.generic') }}
                </VJoyFormError>
                <div class="profile-education__list-item-graduation">
                    <VJoyInput
                        v-model="element.graduationYear"
                        name="graduationYear"
                        :required="true"
                        :invalid="r$.graduationYear.$error"
                        :disabled="isCurrent()"
                        type="number"
                        :placeholder="t('profile.new.certification.graduation.year')"
                    >
                        {{ t('profile.new.certification.graduation.year') }}
                    </VJoyInput>

                    <VJoyCheckbox
                        class="profile-education__list-item-graduation-checkbox"
                        :label="t('newexperience.currentJob')"
                        :checked="!element.graduationYear"
                        @update:checked="(value: boolean) => updateCurrentEducation(value)"
                    />
                </div>
                <VJoyFormError v-if="r$.graduationYear.$error" data-testid="profile-edition-section-education-graduation-year-error">
                    {{ t('form.field.error.message.required.generic') }}
                </VJoyFormError>
                <VJoyTextarea
                    :key="`${element.index}`"
                    v-model="element.content"
                    autogrow
                    :placeholder="t('profile.new.education.content.placeholder')"
                ></VJoyTextarea>
            </VJoyWrapper>
        </template>
    </EditComponent>
</template>

<script setup lang="ts">
    import {required, minValue, maxValue, integer} from '@regle/rules';
    import {storeToRefs} from 'pinia';
    import {ref, useTranslation} from '#imports';
    import {useRegle, validateForm} from '@malt/forms';
    import {VJoyWrapper, VJoyCheckbox, VJoyFormError, VJoyInput, VJoyTextarea} from '@maltjoy/core-vue';
    import EditComponent from '~/components/edit/EditComponent.vue';
    import {type EditionOpenedEntryPoint, EditionOpenedSection} from '~/config/TrackingEvents';
    import {useProfileStore} from '~/store/profileStore';
    import {educationMapper} from '~/types/infra/EducationMapper';
    import type {EducationRequest} from '~/types/infra/EducationRequest';
    import {eventBus} from '@/config/events';

    const {t} = useTranslation();
    const {updateEducations} = useProfileStore();
    const {educations} = storeToRefs(useProfileStore());
    const element = ref<EducationRequest>(defaultValue());
    const index = ref<number>(0);

    const {r$} = useRegle(element, () => ({
        description: {required},
        title: {required},
        graduationYear: element.value?.current ? {} : {required, integer, minValue: minValue(1900), maxValue: maxValue(new Date().getFullYear())},
    }));

    function defaultValue() {
        return {description: '', title: '', graduationYear: 0, content: '', id: 0, current: false};
    }

    function initEducation(data: {educationId: number; entryPoint: EditionOpenedEntryPoint}) {
        r$.$reset();
        index.value = data.educationId;
        const currentEducations = educations.value;
        element.value = {
            ...defaultValue(),
            ...(currentEducations?.items[data.educationId]
                ? educationMapper.toRequest(currentEducations.items[data.educationId], data.educationId)
                : {}),
        };
    }

    function isCurrent() {
        return element.value.current;
    }

    function updateCurrentEducation(value: boolean) {
        element.value.current = value;
    }

    async function update() {
        const {valid} = await validateForm(r$);
        if (!valid) {
            return false;
        }
        const currentEducations = educations.value;
        const educationRequests =
            currentEducations?.items.map((education, idx) => {
                if (idx === index.value) {
                    return element.value;
                }

                return educationMapper.toRequest(education, idx);
            }) || [];

        updateEducations({educations: educationRequests}).then(() => {
            element.value = defaultValue();
            eventBus.emit('educations:updated');
        });
    }
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .draggable {
        cursor: grab;
        margin-left: auto;
    }

    .profile-education__list {
        width: 100%;
        padding-right: 30px; // to leave a small space for scrolling

        @include mq.screen_xxs(min) {
            padding-right: 0;
        }

        &-title {
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
        }

        &-item {
            display: flex;
            flex-direction: column;

            &-graduation {
                display: flex;
                align-items: center;

                &-checkbox {
                    margin-top: var(--joy-core-spacing-6);
                    margin-left: auto;
                }
            }
        }

        &-actions {
            display: flex;
            justify-content: flex-end;
            margin-top: var(--joy-core-spacing-5);

            & > joy-button {
                margin-left: var(--joy-core-spacing-2);
            }
        }
    }
</style>
